import { FormControl, MenuItem, Select, SelectChangeEvent, styled } from "@mui/material";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Role } from "@/enum";

const StyledMenuItem = styled(MenuItem)({
	marginBottom: "0",
	padding: "6px",
	"&.Mui-selected": {
		backgroundColor: "transparent !important",
		color: "inherit",
	},
	"&:hover": {
		backgroundColor: "transparent",
	},
	"& .MuiTypography-root": {
		marginBottom: "0",
	},
});

interface MultiRoleSelectProps {
	selectedRoles: string[];
	onChange: (roles: string[]) => void;
}

export default function MultiRoleSelect({ selectedRoles, onChange }: MultiRoleSelectProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);

	const options = Object.values(Role);
	const [selectedOptions, setSelectedOptions] = useState<string[]>(selectedRoles);
	const allSelected = selectedOptions.length === options.length;

	useEffect(() => {
		if (selectedRoles.filter((role) => !!role).length === 0) {
			onChange(options);
			setSelectedOptions(options);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const label =
		selectedOptions.length === 0 ? (
			ts("none")
		) : (
			<span className="font-urbanist text-[14px] leading-5 text-subtle">
				{ts("role-placeholder")}
				<span className="ml-2 text-[14px] leading-5 text-primary-blue-dark font-medium">{`${
					allSelected ? ts("all") : ts("custom")
				}`}</span>
			</span>
		);

	const handleChange = (event: SelectChangeEvent<string[]>) => {
		let value = event.target.value as string[];

		if (value.includes("All")) {
			value = allSelected ? [] : options;
		}

		if (value.length === 0) {
			value = options;
		}

		setSelectedOptions(value);
		onChange(value);
	};

	return (
		<FormControl sx={{ width: 250 }}>
			<Select
				multiple
				MenuProps={{
					PaperProps: {
						sx: {
							width: "180px",
							borderRadius: "12px",
							padding: "8px",
							mt: "8px",
							border: "1px solid #DCDAD6",
							boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.1)",
						},
					},
					MenuListProps: {
						sx: {
							padding: "0px !important",
							display: "flex",
							flexDirection: "column",
							gap: "8px",
						},
					},
				}}
				renderValue={() => label}
				sx={{
					backgroundColor: "#fff",
					borderRadius: "12px",
					"& .MuiSelect-select": {
						padding: "10px",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#FF5A24 !important",
						borderWidth: "1px !important",
						boxShadow: "none",
					},
					"&.Mui-focused": {
						boxShadow: "none",
					},
				}}
				value={selectedOptions}
				onChange={handleChange}
			>
				<StyledMenuItem value="All">
					{allSelected ? (
						<span className="w-5 h-5 min-w-5 rounded-[4px] flex items-center justify-center bg-primary-blue">
							<Icon className="text-white w-[14px] h-[14px]" icon={faCheck} />
						</span>
					) : (
						<p className="w-5 h-5 min-w-5 rounded-[4px] bg-surface border-[1px] border-grey-soft"></p>
					)}
					<span className="font-urbanist ml-2 text-[14px] leading-5 text-primary-blue-dark font-medium">
						{ts("all")}
					</span>
				</StyledMenuItem>
				{options.map((option) => (
					<StyledMenuItem key={option} value={option}>
						{selectedOptions.includes(option) ? (
							<span className="w-5 h-5 min-w-5 rounded-[4px] flex items-center justify-center bg-primary-blue">
								<Icon className="text-white w-[14px] h-[14px]" icon={faCheck} />
							</span>
						) : (
							<p className="w-5 h-5 min-w-5 rounded-[4px] bg-surface border-[1px] border-grey-soft"></p>
						)}
						<span className="font-urbanist ml-2 text-[14px] leading-5 text-primary-blue-dark font-medium">
							{ts(option)}
						</span>
					</StyledMenuItem>
				))}
			</Select>
		</FormControl>
	);
}
